import { createContext, useContext, useState, useCallback, useRef } from 'react';

const SessionAccContext = createContext(null);

export function useSessionAccumulator() {
  const ctx = useContext(SessionAccContext);
  if (!ctx) throw new Error('useSessionAccumulator must be inside SessionAccProvider');
  return ctx;
}

export function SessionAccProvider({ children }) {
  const [sessionXP, setSessionXP] = useState(0);
  const [eventCount, setEventCount] = useState(0);
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [floats, setFloats] = useState([]); // Array of { id, xp, variant, intensity, origin }
  const floatIdRef = useRef(0);

  const startSession = useCallback(() => {
    setSessionXP(0);
    setEventCount(0);
    setConsecutiveCorrect(0);
    setFloats([]);
    floatIdRef.current = 0;
  }, []);

  // `silent` (default false, backward-compatible) updates the session counters
  // — sessionXP (header badge), eventCount, and the consecutiveCorrect streak
  // the MicroVariantEngine reads — WITHOUT pushing a generic InlineXPFloat. Used
  // by the flawless-write reward (BP-FLAWLESS-XP-REWARD-01), where the hero
  // sequence shows the XP instead, so we must not render both.
  const recordEvent = useCallback(({ xp, variant, intensity, origin, silent = false }) => {
    const id = ++floatIdRef.current;
    setSessionXP(prev => prev + xp);
    setEventCount(prev => prev + 1);
    setConsecutiveCorrect(prev => prev + 1);
    if (!silent) {
      setFloats(prev => [...prev, { id, xp, variant, intensity, origin }]);
    }
    return id;
  }, []);

  const recordMiss = useCallback(() => {
    setConsecutiveCorrect(0);
  }, []);

  const removeFloat = useCallback((id) => {
    setFloats(prev => prev.filter(f => f.id !== id));
  }, []);

  const endSession = useCallback(() => {
    const result = { totalXP: sessionXP, eventCount };
    // Don't reset here — let the toast display the final values
    // Reset happens on next startSession()
    return result;
  }, [sessionXP, eventCount]);

  return (
    <SessionAccContext.Provider value={{
      sessionXP,
      eventCount,
      consecutiveCorrect,
      floats,
      startSession,
      recordEvent,
      recordMiss,
      removeFloat,
      endSession,
    }}>
      {children}
    </SessionAccContext.Provider>
  );
}
